import { Button, Container, Row, Col } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectAuthState, logoutAsync } from 'store/authslice'
import { selectNavigationState, fetchNavigationAsync } from 'store/navigationslice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import * as routes from 'assets/routes'
import NavSection from './NavSection'
import { SearchForm } from 'components/Pages/Search/SearchForm'
import { PersonCircle } from 'react-bootstrap-icons'
import * as c from 'assets/constants'

export const Header = () => {
  const [navigationOpen, setNavigationOpen] = useState<boolean>(false)
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const [myNavigationOpen, setMyNavigationOpen] = useState<boolean>(false)
  const authState = useSelector(selectAuthState)
  const navigationState = useAppSelector(selectNavigationState)
  const dispatch = useAppDispatch()
  const body = document.getElementsByTagName('body')[0] as HTMLElement

  const openSearch = () => {
    setNavigationOpen(false)
    setMyNavigationOpen(false)
    setSearchOpen(!searchOpen)
  }

  const openNavigation = () => {
    setSearchOpen(false)
    setMyNavigationOpen(false)
    setNavigationOpen(!navigationOpen)
  }

  const openMyNavigation = () => {
    setSearchOpen(false)
    setNavigationOpen(false)
    setMyNavigationOpen(!myNavigationOpen)
  }

  const logout = () => {
    dispatch(logoutAsync())
  }

  useEffect(() => {
    dispatch(fetchNavigationAsync())
  }, [])

  useEffect(() => {
    if (navigationOpen || searchOpen || myNavigationOpen) body.classList.add('overflow-hidden')
    else body.classList.remove('overflow-hidden')
  }, [navigationOpen, searchOpen, myNavigationOpen])

  return (
    <>
      <header className="header position-sticky top-0 bg-white shadow-sm">
        <Container fluid="lg" className="d-flex header__container">
          <Link to={'/'} aria-label="Gå till startsidan" className="my-0 my-sm-2 my-md-3">
            <img src={'/images/sll-logo.svg'} height="57" className="logo" alt="Startsida, Region Stockholm" />
          </Link>
          {authState.isAuthenticated ? (
            <Button
              id="search-button"
              variant="light"
              className="menu-button rounded-0 ms-auto d-flex flex-column justify-content-center align-items-center"
              onClick={() => openMyNavigation()}
              aria-expanded={myNavigationOpen ? true : false}
            >
              <PersonCircle />
              <span className="text-capitalize d-none d-md-block">Min sida</span>
            </Button>
          ) : null}
          <Button
            id="search-button"
            variant="tertiary"
            className={`menu-button d-flex flex-column align-items-center justify-content-center rounded-0 lh-1 ${
              !authState.isAuthenticated ? 'ms-auto' : ''
            }`}
            onClick={() => openSearch()}
            aria-expanded={navigationOpen ? true : false}
          >
            <span>Sök</span>
            <span>Utbud</span>
          </Button>
          <Button
            id="navigation-button"
            variant="secondary"
            className="menu-button rounded-0"
            onClick={() => {
              openNavigation()
            }}
            aria-expanded={navigationOpen ? true : false}
          >
            Meny
          </Button>
        </Container>
        {myNavigationOpen && (
          <div className="navigation-foldout bg-light">
            <Container fluid="lg" className="navigation-foldout__container py-5 position-relative">
              <CloseButton callback={() => setMyNavigationOpen(false)} textColor="text-black" />
              <Row>
                <Col>
                  <ul className="list-unstyled d-flex flex-column">
                    <li>
                      <span className="h5 d-block mb-4">
                        Inloggad som: <span className="text-capitalize">{authState.user?.userName}</span>
                      </span>
                    </li>
                    <li>
                      <Link
                        className="d-flex align-items-center"
                        to={routes.MY_PAGE}
                        onClick={() => setMyNavigationOpen(false)}
                      >
                        Min sida
                      </Link>
                    </li>
                    {authState.user?.userRoles?.includes('Utövare') ? (
                      <>
                        {authState.user?.claims ? (
                          <>
                            <li className="mt-2">
                              <Link
                                to={`${routes.CREATE_ACTIVITY}/?orgid=${authState.user?.claims[0]?.value}`}
                                onClick={() => setMyNavigationOpen(false)}
                              >
                                Skapa aktivitet
                              </Link>
                            </li>
                            <li className="mt-2">
                              <Link
                                to={`${routes.EDIT_ORGANISATION}/${authState.user?.claims[0]?.value}`}
                                onClick={() => setMyNavigationOpen(false)}
                              >
                                {c.EDIT_ORGANISATION_TITLE}
                              </Link>
                            </li>
                          </>
                        ) : null}
                      </>
                    ) : null}
                    <li className="py-2 mt-3">
                      <Button onClick={() => logout()} size="sm" className="px-5 text-nowrap">
                        Logga ut
                      </Button>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Container>
          </div>
        )}

        {navigationOpen && (
          <div className="navigation-foldout bg-secondary text-white" aria-labelledby="navigation-button">
            <Container fluid="lg" className="navigation-foldout__container py-5 position-relative">
              <CloseButton callback={() => setNavigationOpen(false)} textColor="text-white" />
              <Row>
                {navigationState.navigationItems &&
                  navigationState.navigationItems.map((navItem, index) => (
                    <NavSection
                      key={`navitem-${index}`}
                      navItem={navItem}
                      callback={() => {
                        setNavigationOpen(false)
                      }}
                    />
                  ))}
              </Row>
            </Container>
          </div>
        )}

        {searchOpen && (
          <div className="navigation-foldout bg-tertiary" aria-labelledby="navigation-button">
            <Container className="py-5 position-relative">
              <CloseButton callback={() => setSearchOpen(false)} textColor={'text-black'} />
              <SearchForm
                setBackgroundTransparentMobile={true}
                performNavigation={true}
                onSearchPerformed={() => {
                  setSearchOpen(false)
                }}
              />
            </Container>
          </div>
        )}
      </header>
      {(navigationOpen || searchOpen || myNavigationOpen) && (
        <div
          className="menu-cover"
          onClick={() => {
            setNavigationOpen(false)
            setSearchOpen(false)
            setMyNavigationOpen(false)
          }}
        ></div>
      )}
    </>
  )
}

//Interface for CloseButton component with text color and callback function as props
export interface CloseButtonProps {
  textColor: string
  callback: () => void
}

// CloseButton component using closebuttonprops interface
const CloseButton = ({ textColor, callback }: CloseButtonProps) => {
  return (
    <Button
      className={`position-absolute top-0 end-0 me-3 mt-3 p-0 btn text-white bg-transparent d-flex align-items-center border-0 ${textColor}`}
      onClick={() => {
        callback()
      }}
    >
      <span className="fs-5 lh-0">Stäng</span>
      <svg
        width="30"
        height="30"
        clipRule="evenodd"
        fillRule="evenodd"
        strokeLinejoin="round"
        strokeMiterlimit="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill={textColor === 'text-white' ? '#fff' : '#000'}
          d="m12 10.93 5.719-5.72c.146-.146.339-.219.531-.219.404 0 .75.324.75.749 0 .193-.073.385-.219.532l-5.72 5.719 5.719 5.719c.147.147.22.339.22.531 0 .427-.349.75-.75.75-.192 0-.385-.073-.531-.219l-5.719-5.719-5.719 5.719c-.146.146-.339.219-.531.219-.401 0-.75-.323-.75-.75 0-.192.073-.384.22-.531l5.719-5.719-5.72-5.719c-.146-.147-.219-.339-.219-.532 0-.425.346-.749.75-.749.192 0 .385.073.531.219z"
        />
      </svg>
    </Button>
  )
}
